/**
 * Stakeholder API Configuration
 * Routes requests to the backend API
 */

import axios from 'axios';
import { getIdToken } from './firebase';

// API base URL — must be set via VITE_API_BASE_URL env var in production
const getStakeholderApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (envUrl && envUrl !== 'undefined') {
    return envUrl;
  }
  if (import.meta.env.PROD) {
    throw new Error('VITE_API_BASE_URL environment variable is required in production');
  }
  return 'http://localhost:3001/api/v2';
};

export const STAKEHOLDER_API_URL = getStakeholderApiUrl();

// Create a dedicated axios instance for stakeholder API
export const stakeholderApi = axios.create({
  baseURL: STAKEHOLDER_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
stakeholderApi.interceptors.request.use(
  async (config) => {
    console.log('[Stakeholder API Interceptor] Request to:', config.url);
    try {
      const token = await getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('[Stakeholder API Interceptor] Token attached successfully');
      } else {
        console.warn('[Stakeholder API Interceptor] No token available for request');
      }
    } catch (error) {
      console.error('[Stakeholder API Interceptor] Failed to get auth token:', error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for consistent error handling
stakeholderApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Handle conversation-specific errors
    const url = error.config?.url || '';
    const isConversationEndpoint = url.includes('/conversations');
    
    // For 500 errors on conversation endpoints, provide better error handling
    if (error.response?.status === 500 && isConversationEndpoint) {
      console.error('Conversation API 500 error:', {
        url,
        method: error.config?.method,
        data: error.config?.data,
        response: error.response?.data
      });
      
      // For GET requests on specific conversations that fail, check if it's a mock stakeholder
      const conversationMatch = url.match(/\/conversations\/([^\/]+)$/);
      if (conversationMatch && error.config?.method === 'get') {
        const conversationId = conversationMatch[1];
        
        // If it's a UUID format conversation that's failing, it might be an orphaned conversation
        if (conversationId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          console.log('UUID conversation not found, likely orphaned:', conversationId);
          // Transform to 404 for better error handling
          error.response.status = 404;
          error.response.data = {
            success: false,
            error: 'Conversation not found or has expired'
          };
        }
      }
    }
    
    const originalRequest = error.config as any;

    // Initialize retry metadata if not present
    if (!originalRequest._retryCount) {
      originalRequest._retryCount = 0;
    }

    // Maximum retry configuration
    const MAX_RETRIES = 3;
    const RETRY_DELAY_BASE = 1000; // 1 second base delay
    const MAX_RETRY_DELAY = 30000; // 30 seconds max delay

    // Don't retry if we've exceeded max retries
    if (originalRequest._retryCount >= MAX_RETRIES) {
      console.error('[Stakeholder API] Max retries exceeded:', {
        url: originalRequest.url,
        retries: originalRequest._retryCount,
        status: error.response?.status
      });
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - Token expiration (only once)
    if (error.response?.status === 401 && !originalRequest._tokenRefreshAttempted) {
      originalRequest._tokenRefreshAttempted = true;
      
      // Token might be expired, try to refresh
      try {
        const newToken = await getIdToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return stakeholderApi.request(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Don't retry further, let it fail
        return Promise.reject(error);
      }
    }

    // Don't retry on client errors
    const NO_RETRY_STATUSES = [400, 401, 403, 404, 422];
    if (error.response && NO_RETRY_STATUSES.includes(error.response.status)) {
      return Promise.reject(error);
    }

    // Handle rate limiting (429)
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const delay = retryAfter ? parseInt(retryAfter) * 1000 : RETRY_DELAY_BASE * Math.pow(2, originalRequest._retryCount);
      
      console.warn('[Stakeholder API] Rate limited. Retrying after delay:', {
        url: originalRequest.url,
        delay: delay / 1000 + 's',
        attempt: originalRequest._retryCount + 1
      });

      originalRequest._retryCount++;
      
      await new Promise(resolve => setTimeout(resolve, Math.min(delay, MAX_RETRY_DELAY)));
      return stakeholderApi.request(originalRequest);
    }

    // Retry on network errors and 5xx server errors
    const shouldRetry = !error.response || (error.response.status >= 500 && error.response.status < 600);
    
    if (shouldRetry && originalRequest._retryCount < MAX_RETRIES) {
      // Calculate exponential backoff delay
      const delay = Math.min(RETRY_DELAY_BASE * Math.pow(2, originalRequest._retryCount), MAX_RETRY_DELAY);
      
      console.warn('[Stakeholder API] Retrying request:', {
        url: originalRequest.url,
        attempt: originalRequest._retryCount + 1,
        maxAttempts: MAX_RETRIES,
        delay: delay / 1000 + 's',
        status: error.response?.status || 'network error'
      });

      originalRequest._retryCount++;
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      return stakeholderApi.request(originalRequest);
    }
    
    return Promise.reject(error);
  }
);

// Export configured endpoints - consolidated to /stakeholders
export const stakeholderEndpoints = {
  // Stakeholder CRUD (consolidated from /personas)
  stakeholders: {
    list: '/stakeholders',
    get: (id: string) => `/stakeholders/${id}`,
    search: '/stakeholders/search',
    gaps: (id: string) => `/stakeholders/${id}/gaps`,
    emotionalState: (id: string) => `/stakeholders/${id}/emotional-state`,
  },

  // Session-based endpoints
  sessions: {
    list: '/stakeholders/sessions/all',
    get: (stakeholderId: string) => `/stakeholders/${stakeholderId}/session`,
    messages: (stakeholderId: string) => `/stakeholders/${stakeholderId}/messages`,
    sendMessage: (stakeholderId: string) => `/stakeholders/${stakeholderId}/messages`,
    sendMessageStream: (stakeholderId: string) => `/stakeholders/${stakeholderId}/messages/stream`,
    reset: (stakeholderId: string) => `/stakeholders/${stakeholderId}/session/reset`,
    resetAll: '/stakeholders/sessions/reset-all',
  },

  // Legacy conversation endpoints removed - use sessions (above) or instructor endpoints instead
};